/**
 * Editor for one CMS node, with a locale switcher.
 *
 * Categories/sections get title + description; articles additionally get the
 * WYSIWYG body, publish state and labels. Edits are held locally and committed
 * on blur/save so typing doesn't thrash localStorage.
 */

import { useState, useEffect, useMemo, type ReactNode } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Badge } from "@/components/ui/badge"
import { Separator } from "@/components/ui/separator"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Trash2, FileText, History } from "lucide-react"
import { RecordStatusBar, type RecordStatus } from "../shared/components/RecordStatusBar"
import { WysiwygEditor } from "./WysiwygEditor"
import * as db from "./store"
import { type KBStore, type AnyNode } from "./types"
import type { NodeVersion } from "./history"
import { HistoryPanel, type HistoryVersionView } from "../shared/components/HistoryPanel"
import { describeLanguage, translationStatus, STATUS_LABEL, type LanguageInfo } from "../shared/languages"
import { isSiteLocale, unsupportedLocaleMessage } from "../shared/siteLanguages"
import { TranslationStatusBar } from "../shared/components/languages/TranslationStatusBar"

interface ArticleNodeEditorProps {
  // Header chip: whether the record is indexed for meaning search
  indexBadge?: ReactNode
  // Where the record stands and what to do next; shown in the strip under the toolbar
  status?: RecordStatus
  store: KBStore
  nodeId: string | null
  locale: string
  /** `summary` describes the edit for the change log. */
  onCommit: (next: KBStore, summary?: string) => void
  // The team's languages, for the switcher
  languages: LanguageInfo[]
  // Lists the working language in Articles, when it isn't yet
  onAddLocale: (locale: string) => void
  /** Recorded versions for one node, newest first. Omit to hide the History button entirely. */
  versionsOf?: (nodeId: string) => NodeVersion[]
  /** Restores a node to a previously-recorded version. */
  restoreNodeVersion?: (nodeId: string, entryId: string) => { ok: true; description: string } | { ok: false; error: string }
}

export function ArticleNodeEditor({
  store, nodeId, locale, onCommit, languages, onAddLocale,
  versionsOf, restoreNodeVersion, indexBadge, status: recordStatus,
}: ArticleNodeEditorProps) {
  const node: AnyNode | null = nodeId ? db.getNode(store, nodeId) : null

  const [title, setTitle] = useState("")
  const [body, setBody] = useState("")
  const [description, setDescription] = useState("")
  const [dirty, setDirty] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [restoringId, setRestoringId] = useState<string | null>(null)

  const tr = useMemo(
    () => (node ? node.translations[locale] : undefined),
    [node, locale, store]
  )

  // Reload fields when the selected node or locale changes.
  useEffect(() => {
    setTitle(tr?.title ?? "")
    setBody(tr?.body ?? "")
    setDescription(tr?.description ?? "")
    setDirty(false)
  }, [nodeId, locale, tr?.updatedAt])

  if (!node) {
    return (
      <div className="flex h-full items-center justify-center p-6 text-center text-sm text-muted-foreground">
        <div>
          <FileText className="mx-auto mb-2 h-8 w-8 opacity-30" />
          Select a category, section or article to edit.
        </div>
      </div>
    )
  }

  const isArticle = node.kind === "article"
  const article = isArticle ? store.articles[node.id] : null
  const path = db.pathOf(store, node.id, locale)
  const exists = !!tr

  // A code the sites can't show, that Articles doesn't list either, can't be written in
  const blocked = !store.locales.includes(locale) && !isSiteLocale(locale)

  // Back to the saved text
  const discard = () => {
    setTitle(tr?.title ?? "")
    setBody(tr?.body ?? "")
    setDescription(tr?.description ?? "")
    setDirty(false)
  }

  const save = () => {
    if (blocked) return
    const next = db.setTranslation(store, node.id, locale, { title, body: isArticle ? body : "", description })
    onCommit(next, `Edited ${node.kind} "${title || "(untitled)"}" (${locale})`)
    setDirty(false)
  }

  const versionViews: HistoryVersionView[] = useMemo(() => {
    if (!versionsOf) return []
    const raw = versionsOf(node.id)
    // The newest non-prior-state entry is what's actually live right now.
    const currentEntryId = raw.find(v => !v.isPriorState)?.entryId
    return raw.map(v => ({
      id: v.entryId,
      at: v.at,
      action: v.action,
      author: v.author,
      actor: v.actor,
      summary: v.summary,
      isCurrent: v.entryId === currentEntryId,
      restoreUnavailable: !v.node,
      snapshot: v.node as Record<string, any> | undefined,
    }))
  }, [versionsOf, node.id, store])

  const handleRestoreVersion = (entryId: string) => {
    if (!restoreNodeVersion) return
    setRestoringId(entryId)
    try {
      const result = restoreNodeVersion(node.id, entryId)
      if (result.ok) setHistoryOpen(false)
    } finally {
      setRestoringId(null)
    }
  }

  const sourceLocale = store.defaultLocale
  const language = languages.find(l => l.code === locale) ?? describeLanguage(locale)
  const source = languages.find(l => l.code === sourceLocale) ?? describeLanguage(sourceLocale)
  const sourceTr = locale === sourceLocale ? undefined : node.translations[sourceLocale]
  const status = translationStatus(node.translations, locale, sourceLocale)


  // The source text as a starting point; it stays an unreviewed draft
  const startFromSource = () => {
    const src = node.translations[sourceLocale]
    if (!src || blocked) return
    onCommit(
      db.setTranslation(store, node.id, locale, {
        title: src.title, body: src.body, description: src.description ?? "", reviewed: false, state: "draft",
      }),
      `Started ${locale} from ${sourceLocale}`,
    )
  }

  const toggleReviewed = () => {
    if (blocked) return
    onCommit(
      db.setTranslation(store, node.id, locale, { reviewed: !tr?.reviewed }),
      `Marked ${locale} as ${tr?.reviewed ? "not reviewed" : "reviewed"}`,
    )
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-wrap items-center gap-2 border-b p-2">
        <Badge variant="outline" className="shrink-0 text-[10px] uppercase">{node.kind}</Badge>
        <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground" title={path}>{path}</span>
        {indexBadge}

        {versionsOf && (
          <Button
            size="sm" variant="ghost" className="h-7 gap-1 text-xs"
            onClick={() => setHistoryOpen(true)}
            title={versionViews.length ? `${versionViews.length} recorded version(s)` : "No recorded changes yet"}
          >
            <History className="h-3 w-3" /> History
            {versionViews.length > 0 && (
              <Badge variant="secondary" className="ml-0.5 h-4 px-1 text-[10px]">{versionViews.length}</Badge>
            )}
          </Button>
        )}

      </div>

      <RecordStatusBar
        status={recordStatus}
        unsaved={dirty ? { onSave: save, onDiscard: discard } : null}
      />

      <TranslationStatusBar
        language={language}
        source={source}
        status={status}
        onToggleReviewed={toggleReviewed}
        onStartFromSource={node.translations[sourceLocale] ? startFromSource : undefined}
        readOnly={blocked}
        unsupported={isSiteLocale(locale) ? undefined : unsupportedLocaleMessage(locale)}
        notListed={store.locales.includes(locale) ? undefined : {
          message: `${language.name} isn't an Articles language yet, so readers can't choose it.`,
          action: "Add to Articles",
          onAdd: () => onAddLocale(locale),
        }}
      >
      </TranslationStatusBar>

      {versionsOf && restoreNodeVersion && (
        <HistoryPanel
          open={historyOpen}
          onOpenChange={setHistoryOpen}
          title={`${node.kind}: ${title || "(untitled)"}`}
          versions={versionViews}
          onRestore={handleRestoreVersion}
          restoringId={restoringId}
        />
      )}

      <ScrollArea className="flex-1 min-h-0 [&_[data-slot=scroll-area-viewport]>:first-child]:block!">
        <div className="space-y-3 p-3">
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Title</label>
            <Input
              dir={language.direction}
              readOnly={blocked}
              value={title}
              onChange={(e) => { setTitle(e.target.value); setDirty(true) }}
              onBlur={() => dirty && save()}
              placeholder={sourceTr?.title || "Title"}
            />
          </div>

          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">
              {isArticle ? "Summary / description" : "Description"}
            </label>
            <Textarea
              dir={language.direction}
              readOnly={blocked}
              value={description}
              onChange={(e) => { setDescription(e.target.value); setDirty(true) }}
              onBlur={() => dirty && save()}
              rows={2}
              placeholder={sourceTr?.description || "Short description"}
              className="text-sm"
            />
          </div>

          {isArticle && (
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Body</label>
              <div dir={language.direction} className={blocked ? "pointer-events-none opacity-60" : undefined}>
                <WysiwygEditor
                  value={body}
                  onChange={(html) => { setBody(html); setDirty(true) }}
                  placeholder={sourceTr?.body ? `Write the ${language.name} article, or start from the ${source.name} text above` : "Write the article…"}
                />
              </div>
            </div>
          )}

          {article && (
            <>
              <Separator />
              <LabelEditor
                labels={article.labels}
                onChange={(labels) => onCommit(
                  db.setArticleLabels(store, node.id, labels),
                  `Set labels: ${labels.join(", ") || "(none)"}`,
                )}
              />
              {article.sourceRef && (
                <p className="text-[10px] text-muted-foreground">
                  Imported from <code>{article.sourceRef}</code>
                </p>
              )}
            </>
          )}

        </div>
      </ScrollArea>
    </div>
  )
}

function LabelEditor({ labels, onChange }: { labels: string[]; onChange: (l: string[]) => void }) {
  const [draft, setDraft] = useState("")
  return (
    <div className="space-y-1">
      <label className="text-xs font-medium text-muted-foreground">Labels</label>
      <div className="flex flex-wrap items-center gap-1">
        {labels.map(l => (
          <Badge key={l} variant="secondary" className="gap-1 text-[10px]">
            {l}
            <button onClick={() => onChange(labels.filter(x => x !== l))} aria-label={`Remove ${l}`}>
              <Trash2 className="h-2.5 w-2.5" />
            </button>
          </Badge>
        ))}
      </div>
      <div className="flex gap-1">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && draft.trim()) {
              e.preventDefault()
              onChange([...labels, draft.trim()])
              setDraft("")
            }
          }}
          placeholder="Add label, press Enter"
          className="h-7 text-xs"
        />
      </div>
    </div>
  )
}
